import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getUser, getUserLoading } from '../../data/selectors/userAuthSelectors';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';
import { chatRequest } from '../../services/request';
import { geminiChatRequest } from '../../services/geminiChat';
import { getConversation } from '../../services/conversations';
import styles from './chat.module.css';
import { FaArrowCircleUp, FaRegCopy, FaCheck } from 'react-icons/fa';
import { HiOutlineSparkles } from 'react-icons/hi2';
import { LuMessageCircleWarning } from 'react-icons/lu';
import { History, Plus } from 'lucide-react';
import MarkdownContent from './MarkdownContent';
import ChatHistoryDrawer from './ChatHistoryDrawer';
import { getDocTypeByStudyUuid, getSingleSummarizedStudy } from '../../services/studies';

export default function Chat() {
  const userLoading = useSelector(getUserLoading);
  const user = useSelector(getUser);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerRefreshKey, setDrawerRefreshKey] = useState(0);
  const [ragMode, setRagMode] = useState('pinecone'); // 'pinecone' | 'gemini'
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    document.title = 'KNOW - AI-Assisted Discovery';
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !user._id) return;

    // Restore saved height on mount
    const storageKey = `chat-messages-height-${user._id}`;
    const savedHeight = localStorage.getItem(storageKey);
    if (savedHeight) {
      container.style.height = savedHeight;
    }

    // Save height changes when user resizes
    let isInitialObservation = true;
    const observer = new ResizeObserver((entries) => {
      if (isInitialObservation) {
        isInitialObservation = false;
        return;
      }
      const entry = entries[0];
      const height = entry.contentRect.height;
      if (height > 0) {
        localStorage.setItem(storageKey, `${height}px`);
      }
    });

    observer.observe(container);
    return () => observer.disconnect();
  }, [user._id]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea || !user._id) return;

    // Restore saved textarea height on mount
    const storageKey = `chat-input-height-${user._id}`;
    const savedHeight = localStorage.getItem(storageKey);
    if (savedHeight) {
      textarea.style.height = savedHeight;
    }

    // Save height changes when user resizes
    let isInitialObservation = true;
    const observer = new ResizeObserver((entries) => {
      if (isInitialObservation) {
        isInitialObservation = false;
        return;
      }
      const entry = entries[0];
      const height = entry.contentRect.height;
      if (height > 0) {
        localStorage.setItem(storageKey, `${height}px`);
      }
    });

    observer.observe(textarea);
    return () => observer.disconnect();
  }, [user._id]);

  // Focus the input on initial page load once user is ready
  useEffect(() => {
    if (!userLoading && user.email) {
      inputRef.current?.focus();
    }
  }, [userLoading]);

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const userMessage = {
      role: 'user',
      content: inputMessage.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    if (ragMode === 'gemini') {
      // Gemini mode - non-streaming, no conversation persistence
      // Add empty assistant message immediately to show loading animation
      const assistantMessage = {
        role: 'assistant',
        content: '',
        citationMap: new Map(),
      };
      setMessages((prev) => [...prev, assistantMessage]);
      setIsStreaming(true);

      try {
        const { data } = await geminiChatRequest([...messages, userMessage]);

        // Build citation map from grounding metadata
        const citationMap = new Map();
        const citationMarkers = [];
        if (data.groundingMetadata?.groundingChunks) {
          data.groundingMetadata.groundingChunks.forEach((chunk, index) => {
            const refNum = index + 1;
            const uuid = chunk.retrievedContext?.customMetadata?.find(
              (m) => m.key === 'uuid'
            )?.stringValue;
            if (uuid) {
              citationMap.set(refNum, [uuid]);
              citationMarkers.push(`[^${refNum}]`);
            }
          });
        }

        // Append citation markers to text so copy function can find references
        let content = data.text || '';
        if (citationMarkers.length > 0) {
          content += '\n\n' + citationMarkers.join(' ');
        }

        // Update the assistant message with the response
        setMessages((prev) => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          lastMessage.content = content;
          lastMessage.citationMap = citationMap;
          return newMessages;
        });
      } catch (error) {
        console.error('Error sending Gemini message:', error);
        const errorMessage = error.response?.message || 'Failed to get response. Please try again.';
        setMessages((prev) => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          lastMessage.content = `\n\n⚠️ *${errorMessage}*`;
          return newMessages;
        });
      } finally {
        setIsLoading(false);
        setIsStreaming(false);
        // Defer focus until after React re-renders the textarea as enabled
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    } else {
      // Pinecone mode - streaming with conversation persistence
      setIsStreaming(true);
      try {
        // Initialize an empty assistant message with its own citation map
        const assistantMessage = {
          role: 'assistant',
          content: '',
          citationMap: new Map(),
        };

        setMessages((prev) => [...prev, assistantMessage]);

        // Update the message content as chunks arrive
        await chatRequest(
          [...messages, userMessage],
          (chunk) => {
            if (chunk.type === 'content') {
              setMessages((prev) => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                lastMessage.content += chunk.text;
                return newMessages;
              });
            } else if (chunk.type === 'citation') {
              setMessages((prev) => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage.role === 'assistant') {
                  lastMessage.citationMap = new Map(lastMessage.citationMap);
                  lastMessage.citationMap.set(chunk.ref, chunk.uuids);
                }
                return newMessages;
              });
            } else if (chunk.type === 'end') {
              if (chunk.conversationId) {
                setConversationId(chunk.conversationId);
              }
              setDrawerRefreshKey((prev) => prev + 1);
            } else if (chunk.type === 'error') {
              // Append error message to content
              setMessages((prev) => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                lastMessage.content += `\n\n⚠️ *${chunk.message}*`;
                return newMessages;
              });
            }
          },
          conversationId
        );
      } catch (error) {
        console.error('Error sending message:', error);
        setMessages((prev) => {
          const newMessages = [...prev];
          const lastMessage = newMessages[newMessages.length - 1];
          if (lastMessage.role === 'assistant') {
            lastMessage.content += '\n\n⚠️ *Failed to get response. Please try again.*';
          }
          return newMessages;
        });
      } finally {
        setIsLoading(false);
        setIsStreaming(false);
        // Defer focus until after React re-renders the textarea as enabled
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    }
  };

  const handleNewChat = () => {
    setConversationId(null);
    setMessages([]);
    // Note: No server call needed for Gemini mode (ephemeral)
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleLoadConversation = async (id) => {
    setIsLoading(true);
    try {
      const result = await getConversation(id);
      const conv = result.data;
      setConversationId(conv.id);

      const loadedMessages = conv.messages.map((msg) => {
        const mapped = { role: msg.role, content: msg.content };
        if (msg.role === 'assistant' && msg.citations?.citations) {
          const cMap = new Map();
          msg.citations.citations.forEach((cit) => {
            cMap.set(cit.ref, cit.uuids);
          });
          mapped.citationMap = cMap;
        }
        return mapped;
      });

      setMessages(loadedMessages);
    } catch (err) {
      console.error('Failed to load conversation:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyMessage = async (messageContent, citationMap, index) => {
    try {
      // Find all citation markers [^n] in the raw markdown content
      const citationRegex = /\[\^(\d+)\]/g;
      const refNumbers = new Set();
      let match;

      while ((match = citationRegex.exec(messageContent)) !== null) {
        refNumbers.add(parseInt(match[1], 10));
      }

      // Build maps for refNum -> data
      const refToUrl = {};
      const refToStudyData = {};
      await Promise.all(
        [...refNumbers].map(async (refNum) => {
          const uuids = citationMap?.get?.(refNum);
          if (!uuids?.length) return;
          try {
            // Fetch full study data for reference and link fields
            const { data: studyData } = await getSingleSummarizedStudy(uuids[0]);
            const study = studyData.results;
            refToStudyData[refNum] = study;
            refToUrl[refNum] =
              `https://knowintegrativeoncology.org/shared?uuid=${uuids[0]}&docType=${study.docType}`;
          } catch (err) {
            console.error('Error resolving citation data:', err);
          }
        })
      );

      // Convert [^n] to [n] in the content
      let processedContent = messageContent.replace(/\[\^(\d+)\]/g, '[$1]');

      // Build KNOW Summary Links section (URLs)
      const sortedRefs = [...refNumbers].sort((a, b) => a - b);
      const referencesWithUrls = sortedRefs.filter((refNum) => refToUrl[refNum]);

      if (referencesWithUrls.length > 0) {
        const knowSummariesSection = referencesWithUrls
          .map((refNum) => `[${refNum}]: ${refToUrl[refNum]}`)
          .join('\n');
        processedContent += `\n\nKNOW Summary Links:\n${knowSummariesSection}`;
      }

      // Build References section (citation text and web links)
      const referencesWithData = sortedRefs.filter((refNum) => refToStudyData[refNum]?.reference);

      if (referencesWithData.length > 0) {
        const referencesSection = referencesWithData
          .map((refNum) => {
            const study = refToStudyData[refNum];
            const referenceText = study.reference.replace(/doi.*[\s]?/, '').trim();
            // Extract only the first HTTP(S) link from comma-separated links
            const webLinkMatch = study.link?.match(/https?:\/\/[^,\s]*/);
            const webLink = webLinkMatch ? webLinkMatch[0] : null;
            if (webLink) {
              return `[${refNum}]: ${referenceText}\n${webLink}`;
            }
            return `[${refNum}]: ${referenceText}`;
          })
          .join('\n\n');
        processedContent += `\n\nReferences:\n${referencesSection}`;
      }

      await navigator.clipboard.writeText(processedContent);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy message:', err);
    }
  };

  const handleLinkClick = async (e, messageCitationMap) => {
    const element = e.target;

    // Only handle citation links (superscript numbers in brackets)
    if (!element.classList.contains('citation-link')) {
      // For regular links, open in a new tab
      e.preventDefault();
      if (element.href) {
        window.open(element.href, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    e.preventDefault();

    const refText = element.textContent;
    const match = refText.match(/\[(\d+)\]/);
    if (!match) return;

    const refNum = parseInt(match[1], 10);
    const citationMap = messageCitationMap || new Map();
    const uuids = citationMap.get(refNum);

    if (!uuids || !uuids.length) return;

    // Use first UUID (primary reference)
    const uuid = uuids[0];

    try {
      const {
        data: { docType },
      } = await getDocTypeByStudyUuid(uuid);
      if (!docType) {
        throw new Error('Could not determine document type');
      }

      // Construct the shared URL
      const sharedUrl = `https://knowintegrativeoncology.org/shared?uuid=${uuid}&docType=${docType}`;

      // Open in a new tab
      window.open(sharedUrl, '_blank');
    } catch (error) {
      console.error('Error fetching study details:', error);
      // Optionally show an error message to the user
    }
  };

  if (userLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className={styles.chatContainer} role="main" aria-label="'KNOW AI'ai-assisted discovery">
      <div className={styles.chatHeader}>
        <h1>
          KNOW AI{' '}
          <span className={styles.chatHeading}>
            <HiOutlineSparkles />
          </span>
        </h1>
        <div className={styles.headerControls}>
          {user.role === 'know-admin' && (
            <div className={styles.modeToggle}>
              <label className={styles.toggleLabel}>
                <input
                  type="radio"
                  name="ragMode"
                  value="pinecone"
                  checked={ragMode === 'pinecone'}
                  onChange={() => setRagMode('pinecone')}
                />
                <span>Pinecone</span>
              </label>
              <label className={styles.toggleLabel}>
                <input
                  type="radio"
                  name="ragMode"
                  value="gemini"
                  checked={ragMode === 'gemini'}
                  onChange={() => setRagMode('gemini')}
                />
                <span>Gemini</span>
              </label>
            </div>
          )}
          <button
            className={styles.newChatHeaderButton}
            onClick={handleNewChat}
            title="New chat"
            aria-label="Start a new chat"
          >
            <Plus size={16} /> New Chat
          </button>
          {ragMode === 'pinecone' && (
            <button
              className={styles.historyButton}
              onClick={() => setIsDrawerOpen(true)}
              title="Chat history"
              aria-label="Open chat history"
            >
              <History size={24} />
            </button>
          )}
        </div>
      </div>

      <div ref={messagesContainerRef} className={styles.messagesContainer}>
        {messages.map((message, index) => (
          <div
            key={index}
            className={`${styles.message} ${
              message.role === 'user' ? styles.user : styles.assistant
            }`}
          >
            <MarkdownContent
              content={message.content}
              onLinkClick={(e) => handleLinkClick(e, message.citationMap)}
            />
            {isStreaming && index === messages.length - 1 && message.role === 'assistant' && (
              <span className={styles.loadingDots}></span>
            )}
            {message.role === 'assistant' && !isStreaming && message.content && (
              <button
                className={styles.copyButton}
                onClick={() => handleCopyMessage(message.content, message.citationMap, index)}
                title="Copy to clipboard including links"
                aria-label="Copy message to clipboard"
              >
                {copiedIndex === index ? <FaCheck /> : <FaRegCopy />}
              </button>
            )}
          </div>
        ))}
        {isLoading && !isStreaming && (
          <div className={styles.loadingMessage}>
            <LoadingSpinner />
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className={styles.inputForm}>
        <textarea
          value={inputMessage}
          ref={inputRef}
          onChange={(e) => setInputMessage(e.target.value)}
          placeholder="Prompt the AI assistant here..."
          className={styles.input}
          rows="2"
          disabled={isLoading}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              handleSubmit(e);
            }
          }}
        />
        <button
          type="submit"
          className={styles.sendButton}
          disabled={isLoading || !inputMessage.trim()}
        >
          <FaArrowCircleUp style={{ fontSize: '2em' }} />
        </button>
      </form>
      <div className={styles.disclaimer}>
        <p>
          <span className={styles.attentionIcon} style={{ color: '#683266' }}>
            <LuMessageCircleWarning />
          </span>
          <i>
            AI-Assisted Discovery is experimental and should not be relied upon exclusively.
            <br />
            Please review original summaries and source materials.
          </i>
        </p>
      </div>

      <ChatHistoryDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onNewChat={handleNewChat}
        onLoadConversation={handleLoadConversation}
        currentConversationId={conversationId}
        refreshKey={drawerRefreshKey}
      />
    </div>
  );
}
